import { useMemo } from 'react';
import {
  balanceOf,
  computeDay,
  readHandIn,
  handIn,
  handled,
  moneyOf,
  readRoute,
  leftUnstarted,
  toCollect,
  weekdayOf,
  dayOf,
  type Alert,
  type Courier,
  type CourierDay,
  type Day,
  type HandIn,
  type Route,
  type Stop,
  type Tally,
  type Doc,
  isLeft,
  lastSign,
  notDeliveredAlerts,
  readTally,
  tallyDays,
  dayStart,
  addDays,
} from '@day';
import { useStore } from '../app/store';
import { useNow } from '../app/clock';
import { ago, askedFor, computerCountry, courierFirst, dateStyleOf, dayBefore, dayTime, distance, money, plural, quietFor, reasonWords, streetOf, time, unitOf, useDateStyle, useUnit } from '../app/words';
import { data, onCallOf, readSentStop, type Business, type BusinessPlace, type Data, type SentStop } from './live';
import { overlay, overlayMap, overlayWaiting, pending, type Pending } from './pending';
import { readWaiting, type Waiting } from './waiting';
import { readZone, type Zone } from './zones';
import { estimates as estimate, straight } from './routing';

const EMPTY_PENDING: Pending = { routes: new Map(), sentStops: new Map(), cleared: new Set(), handIns: new Map(), waiting: new Map(), zones: new Map() };

/**
 * What the pages read: today worked out by the shared engine (`@day`, the phone's rules), with each courier's colour
 * and every Needs a look line in the phone's own words (live_screen.dart).
 */

/** Six colours colour-blind owners can tell apart, never green, red or amber (courier_colours.dart), in joining order. */
export const COURIER_COLOURS: { hex: string; on: string }[] = [
  { hex: '#0379F0', on: '#FFFFFF' },
  { hex: '#9F0176', on: '#FFFFFF' },
  { hex: '#47B5FA', on: '#15130F' },
  { hex: '#5C39C1', on: '#FFFFFF' },
  { hex: '#E08DB2', on: '#15130F' },
  { hex: '#9369AC', on: '#FFFFFF' },
];
/** A courier who isn't in the list any more: the ink disc. */
export const NO_COLOUR = { hex: '#5E594F', on: '#FFFFFF' };

export function coloursOf(couriers: Courier[]): Map<string, { hex: string; on: string }> {
  const order = [...couriers].sort((a, b) =>
    a.joinedAt != null && b.joinedAt != null ? a.joinedAt - b.joinedAt : a.joinedAt != null ? -1 : b.joinedAt != null ? 1 : a.uid.localeCompare(b.uid),
  );
  return new Map(order.map((c, i) => [c.uid, COURIER_COLOURS[i % COURIER_COLOURS.length]]));
}

export interface Person {
  uid: string;
  courier: Courier;
  first: string;
  name: string;
  hex: string;
  on: string;
  day: CourierDay | null;
  /** Their balance with the business: above zero, the business's money they hold (`CourierBalance.of`: routes ended). */
  balance: number;
  /** What the route they're on has taken for the business so far: added to the balance when it ends, never before. */
  onRoute: number;
  waitingHandIns: HandIn[];
  onCall: boolean;
}

/** A line in Needs a look: an alert from the engine, a sent route or stop, or a hand-in to confirm. */
export interface Look {
  id: string;
  kind: Alert['kind'] | 'declined' | 'stale' | 'stopDeclined' | 'stopStale' | 'handIn';
  act: boolean;
  icon: string;
  title: string;
  detail: string;
  person: Person | null;
  route: Route | null;
  stop: Stop | null;
  alert: Alert | null;
  /** Whether "Clear with a note" is allowed (the rules take only these alert kinds). */
  clearable: boolean;
  /** A courier's stops not delivered in the last 28 days, to read (Needs a look's "Musa: 3 stops not delivered"), one line each. */
  missed?: string[];
}

export interface Model {
  ready: boolean;
  error: string | null;
  business: Business | null;
  ownerName: string | null;
  zone: string;
  now: number;
  today: string;
  day: Day;
  people: Person[];
  person: (uid: string) => Person | null;
  routes: Route[];
  route: (id: string) => Route | null;
  /** Every stop on today's routes and those waiting. */
  stops: Stop[];
  stop: (key: string) => Stop | null;
  looks: Look[];
  /** Routes being sent or changed: shown now, written after their 5 seconds. */
  sending: Set<string>;
  /** A route's record as stored (with what's pending), for writes that send it on. */
  raw: (id: string) => Doc | null;
  /** Where routes start, as the owner set it (`businesses/{id}.places`, the first): its name and address. */
  place: BusinessPlace | null;
  /**
   * Where routes start on the map and for timing: the place's name, at the newest sent route's start (places keep no
   * position, for Google's 30 days). Null when the owner removed it, or with no route sent from anywhere yet.
   */
  shop: { lat: number; lng: number; label: string; address?: string } | null;
  sentStops: SentStop[];
  currency: string;
  /** What couriers hold, and what's owed to couriers. */
  handInTotal: number;
  owedTotal: number;
  /** The last 60 days' routes, finished; null until read. */
  history: Route[] | null;
  /** Each day's numbers (the business's days), from the past days and today's finished routes. */
  tallies: Map<string, { all: Tally; byCourier: Map<string, Tally> }>;
  /** The past days whose numbers are the server's records (functions/src/daily_totals.ts), not worked out here. */
  serverDays: Set<string>;
  todayStart: number;
  yesterdayStart: number;
  /** Stops waiting for a route, newest first (docs/waiting-stops.md). */
  waiting: Waiting[];
  /** The zones the owner drew, oldest first, each in its first courier's colour (neutral with none). */
  zones: ZoneShown[];
  /** A route waiting to start, timed as if it left now: each stop's arrival by key, and the route's finish, length and time. */
  ifNow: { stops: Map<string, number>; routes: Map<string, { finish: number; distanceM: number; durationS: number; google: boolean; back: boolean }> };
  data: Data;
}

/** A zone as the pages draw it: its colour is its first courier's still in the business, else the ink. */
export interface ZoneShown extends Zone {
  hex: string;
  on: string;
}

/** A stop's key: a stop's id is only unique on its own route. */
export const keyOf = (s: Pick<Stop, 'routeId' | 'id'>) => `${s.routeId}/${s.id}`;

const DECLINE: Record<string, string> = {
  onAnotherRoute: 'On another route',
  tooFar: 'Too far from the start',
  vehicle: 'Problem with their vehicle',
};
const declineText = (r: string | null) => DECLINE[r ?? ''] ?? 'Something else';

/** "Lunch run", or for one not named "Thursday’s route" (SentRoute.title). */
export function routeTitle(r: Route, zone: string): string {
  if (r.name) return r.name;
  const t = r.sentAt ?? r.startedAt;
  if (t == null) return 'The route';
  return `${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][weekdayOf(dayOf(t, zone))]}’s route`;
}

/** "the Lunch run" in a sentence. */
export const routeInSentence = (r: Route, zone: string) => (r.name ? `the ${r.name}` : routeTitle(r, zone));

/** What the courier holds for the business from the route they're running now (MoneyTotals.handIn). */
function runningHandIn(r: Route | null, onCall: boolean): number {
  if (!r) return 0;
  return handIn(moneyOf(r.stops.filter(handled), onCall));
}

export function buildModel(raw: Data, now: number, p: Pending = EMPTY_PENDING): Model {
  // What the owner has just done, laid over Firestore's copy until it's written.
  const d: Data = {
    ...raw,
    routes: overlay(raw.routes, p),
    cleared: p.cleared.size ? new Set([...raw.cleared, ...p.cleared]) : raw.cleared,
    sentStops: p.sentStops.size
      ? [...raw.sentStops.filter((x) => !p.sentStops.has(x.id)), ...[...p.sentStops].filter(([, doc]) => doc).map(([id, doc]) => readSentStop(id, doc!))]
      : raw.sentStops,
    handIns: p.handIns.size ? [...raw.handIns.filter((h) => !p.handIns.has(h.id)), ...[...p.handIns].map(([id, doc]) => readHandIn(id, doc)).filter((h): h is HandIn => !!h)] : raw.handIns,
  };
  const business = d.business;
  // Distances, dates and times as the business's country writes them (miles in the UK and the US; "Friday, October 30"
  // and the 12- or 24-hour clock), from the country the owner confirmed, else this computer's, for show only.
  const country = business?.country ?? computerCountry();
  useUnit(unitOf(country));
  useDateStyle(dateStyleOf(country));
  const zone = business?.zone ?? 'Africa/Lagos';
  const today = dayOf(now, zone);
  const onCall = onCallOf(business);
  const routes = [...d.routes.entries()].map(([id, doc]) => readRoute(id, doc, zone)).filter((r): r is Route => !!r);
  const day = computeDay({ routes, couriers: d.couriers, presence: d.presence, cleared: d.cleared, now, zone, onCall });
  const colours = coloursOf(d.couriers);
  const byDay = new Map(day.couriers.map((c) => [c.courier.uid, c]));
  const people: Person[] = d.couriers.map((c) => {
    const b = balanceOf(c.uid, d.routeMoney, d.handIns);
    const colour = colours.get(c.uid) ?? NO_COLOUR;
    return {
      uid: c.uid,
      courier: c,
      first: courierFirst(c),
      name: c.name?.trim() || c.phone || 'A courier',
      hex: colour.hex,
      on: colour.on,
      day: byDay.get(c.uid) ?? null,
      // As the phone has it: a route's money counts once it ends (routeMoney), so a hand-in recorded now settles that.
      balance: b.amount,
      onRoute: runningHandIn(byDay.get(c.uid)?.running ?? null, onCall(c.uid)),
      waitingHandIns: b.waiting,
      onCall: onCall(c.uid),
    };
  });
  // In the order Live lists them: on a route first, then by name (live_today.dart).
  const order = new Map(day.couriers.map((c, i) => [c.courier.uid, i]));
  people.sort((a, b) => (order.get(a.uid) ?? 99) - (order.get(b.uid) ?? 99));
  const personMap = new Map(people.map((p) => [p.uid, p]));
  const person = (uid: string) => personMap.get(uid) ?? null;
  const routeMap = new Map(day.routes.map((r) => [r.id, r]));
  const stops = day.routes.flatMap((r) => r.stops);
  const stopMap = new Map(stops.map((s) => [keyOf(s), s]));
  // A route still open from an earlier day, or one that started before midnight and ended after it, isn't today's
  // (live_provider.dart), but its courier's row, Needs a look and the menus open it: findable by id, not listed.
  for (const c of day.couriers) {
    for (const r of [c.leftOpen, ...c.finished]) {
      if (!r || routeMap.has(r.id)) continue;
      routeMap.set(r.id, r);
      for (const x of r.stops) stopMap.set(keyOf(x), x);
    }
  }
  const stopOf = (routeId: string, stopId?: string) => (stopId ? (stopMap.get(`${routeId}/${stopId}`) ?? null) : null);
  const first = (uid: string) => person(uid)?.first ?? 'A courier';

  const looks: Look[] = [];
  for (const a of day.alerts) {
    const route = routeMap.get(a.routeId) ?? (d.routes.has(a.routeId) ? readRoute(a.routeId, d.routes.get(a.routeId)!, zone) : null);
    const stop = stopOf(a.routeId, a.stopId) ?? route?.stops.find((s) => s.id === a.stopId) ?? null;
    const name = first(a.courierId);
    const street = stop ? streetOf(stop) : 'A stop';
    let title = '';
    let detail = '';
    let icon = 'warningCircle';
    switch (a.kind) {
      case 'late':
        icon = 'clock';
        title = `${street} may miss its ${time(stop?.deliverBy, zone)}`;
        detail = `${name} · now about ${time(a.expected, zone)}`;
        break;
      case 'failedInARow': {
        icon = 'xCircle';
        title = `${name}: ${a.failures?.length ?? 2} not delivered in a row`;
        const r = routeMap.get(a.routeId);
        detail = (a.failures ?? [])
          .map((f) => {
            const s = r?.stops.find((x) => x.id === f.stopId) ?? stops.find((x) => x.id === f.stopId && x.riderId === a.courierId);
            return `${reasonWords(s?.reason)} ${time(f.at, zone).replace(/ [AP]M$/, '')}`;
          })
          .join(' · ');
        break;
      }
      case 'quiet':
        icon = 'warning';
        title = `No position from ${name} for ${quietFor(now - (a.lastHeard ?? now))}`;
        detail = `Last at ${time(a.lastHeard, zone)}. Routr may be closed, or no signal.`;
        break;
      case 'farFromAddress':
        icon = 'mapPin';
        title = `${street} marked delivered about ${distance(a.metres ?? 0)} away`;
        detail = `${name} · ${time(a.handled, zone)}. Where ${name}’s phone was, against the address.`;
        break;
      case 'unpaid':
        icon = 'money';
        title = `${street} wasn’t paid`;
        detail = `${stop ? money(toCollect(stop), stop.currency ?? business?.payments.currency) : ''} · ${name} left it with them · ${time(a.handled, zone)}`;
        break;
      case 'anotherDay':
        icon = 'calendarBlank';
        title = `${stop?.name?.split(/\s+/)[0] ?? street} asked for ${askedFor(a.day, today)}`;
        detail = `${name} · ${street} · ${time(a.handled, zone)}. Plan it for that day.`;
        break;
      case 'stillOpen':
        icon = 'path';
        title = `${name}’s route is still open`;
        detail = [
          `From ${a.handled != null ? dayBefore(dayOf(a.handled, zone), today) : 'earlier'}`,
          a.left ? `${plural(a.left, 'stop')} not marked` : '',
          a.lastHeard != null ? `last heard ${dayTime(a.lastHeard, now, zone)}` : '',
        ]
          .filter(Boolean)
          .join(' · ');
        break;
      case 'notAttempted':
        icon = 'flagCheckered';
        title = `${plural(a.left ?? 0, 'stop')} not attempted`;
        detail = a.courierId.startsWith('former-')
          ? `The route ended at ${time(a.handled, zone)}. Its courier’s account has been deleted. Send them again if they’re still wanted.`
          : `${name} ended the route at ${time(a.handled, zone)}. Not counted against ${name}; send them again if they’re still wanted.`;
        break;
    }
    looks.push({ id: a.id, kind: a.kind, act: a.severity === 'needsAction', icon, title, detail, person: person(a.courierId), route, stop, alert: a, clearable: true });
  }
  // Sent routes a courier couldn't take, and those left unstarted (live_screen.dart `_SentRow`).
  for (const r of day.routes) {
    if (r.state !== 'waiting' && r.state !== 'declined') continue;
    const name = first(r.riderId);
    const seen = r.orderChangedAt != null ? `order changed by ${name}` : r.seenAt != null ? `seen ${time(r.seenAt, zone)}` : null;
    if (r.state === 'declined') {
      looks.unshift({
        id: `declined:${r.id}`,
        kind: 'declined',
        act: true,
        icon: 'warningCircle',
        title: `${name} can’t take ${routeInSentence(r, zone)}`,
        detail: [declineText(r.declineReason), r.declineNote ? `“${r.declineNote}”` : '', r.declinedAt ? time(r.declinedAt, zone) : ''].filter(Boolean).join(' · '),
        person: person(r.riderId),
        route: r,
        stop: null,
        alert: null,
        clearable: false,
      });
    } else {
      const busy = person(r.riderId)?.day;
      if (leftUnstarted(r, now, busy?.running ? busy.finishAt : null)) {
        looks.push({
          id: `stale:${r.id}`,
          kind: 'stale',
          act: false,
          icon: 'paperPlaneTilt',
          title: `${routeTitle(r, zone)} not started`,
          detail: [`Sent to ${name} ${Math.round((now - (r.sentAt ?? now)) / 60000)} min ago`, seen].filter(Boolean).join(' · '),
          person: person(r.riderId),
          route: r,
          stop: null,
          alert: null,
          clearable: false,
        });
      }
    }
  }
  // Single stops sent: one a courier can't take, or one not added in half an hour.
  for (const x of d.sentStops) {
    const s = x.data.stop as Record<string, unknown> | undefined;
    const where = typeof s?.address === 'string' ? s.address.split(',')[0].trim() : 'A stop';
    const name = first(x.riderId);
    if (x.status === 'declined') {
      looks.unshift({
        id: `stopDeclined:${x.id}`,
        kind: 'stopDeclined',
        act: true,
        icon: 'warningCircle',
        title: `${name} can’t take ${where}`,
        detail: declineText(typeof x.data.declineReason === 'string' ? x.data.declineReason : null),
        person: person(x.riderId),
        route: null,
        stop: null,
        alert: null,
        clearable: false,
      });
    } else if (x.status === 'sent' && x.sentAt != null && now - x.sentAt > 30 * 60000) {
      looks.push({
        id: `stopStale:${x.id}`,
        kind: 'stopStale',
        act: false,
        icon: 'clock',
        title: `${where} not added`,
        detail: `Sent to ${name} ${Math.round((now - x.sentAt) / 60000)} min ago`,
        person: person(x.riderId),
        route: null,
        stop: null,
        alert: null,
        clearable: false,
      });
    }
  }
  // Hand-ins a courier says they made, waiting for the owner to confirm (settling-up.html).
  for (const p of people) {
    for (const h of p.waitingHandIns) {
      looks.push({
        id: `handIn:${h.id}`,
        kind: 'handIn',
        act: false,
        icon: 'money',
        title: `${p.first} says they handed in ${money(h.amount, business?.payments.currency)}`,
        detail: `${h.how === 'transfer' ? 'By transfer' : 'In cash'}${h.at ? ` · ${ago(h.at, now)}` : ''}. Confirm what came in.`,
        person: p,
        route: null,
        stop: null,
        alert: null,
        clearable: false,
      });
    }
  }
  looks.sort((a, b) => Number(b.act) - Number(a.act));

  // Where routes start: the newest sent route's start, as the owner planned it.
  const withStart = [...d.routes.entries()]
    .map(([id, doc]) => readRoute(id, doc, zone))
    .filter((r): r is Route => !!r && !!r.start && r.sentAt != null)
    .sort((a, b) => (b.sentAt ?? 0) - (a.sentAt ?? 0));
  const guess = withStart[0]?.start ?? null;
  const places = business?.places ?? null;
  const place = places?.[0] ?? null;
  const shop = places && !places.length ? null : guess ? { ...guess, label: place?.label ?? guess.label, ...(place ? { address: place.address } : {}) } : null;

  const history = d.history ? [...d.history.entries()].map(([id, doc]) => readRoute(id, doc, zone)).filter((r): r is Route => !!r && (r.state === 'done' || r.state === 'ended')) : null;
  // Every route read live counts too, newer than the copy read for the past days: one started before midnight and
  // finished today, as much as today's. A route left running counts once it ends (as the server's days count it).
  // A past route opens from a courier's record (Recent routes): findable by id, not listed, as a route left open is.
  for (const r of history ?? []) {
    if (routeMap.has(r.id)) continue;
    routeMap.set(r.id, r);
    for (const x of r.stops) stopMap.set(keyOf(x), x);
  }
  const past = new Map((history ?? []).map((r) => [r.id, r]));
  for (const r of routes) {
    if (r.state === 'done' || r.state === 'ended' || (r.state === 'running' && !isLeft(lastSign(r), now, zone))) past.set(r.id, r);
    else if (r.state === 'running') past.delete(r.id);
  }
  const tallies = tallyDays([...past.values()], zone, { running: true });
  // Each courier's stops not delivered in the last 28 days (the phone's notDelivered), from the past days' routes and today's.
  for (const a of notDeliveredAlerts([...past.values()], d.couriers, now, d.decided)) {
    const p = person(a.courierId);
    const found = (a.missed ?? []).flatMap((x) => {
      const route = past.get(x.routeId);
      const stop = route?.stops.find((s) => s.id === x.stopId);
      return route && stop ? [{ route, stop, at: x.at ?? route.endedAt ?? route.completedAt ?? route.startedAt ?? now, notReached: x.notReached }] : [];
    });
    if (!found.length) continue;
    const oldest = Math.min(...found.map((x) => x.at));
    // "12 Danube Street · No one there · Tuesday", or "Not reached" on a route ended before it.
    const missed = found.map((x) => [streetOf(x.stop), x.notReached ? 'Not reached' : reasonWords(x.stop.reason), dayBefore(dayOf(x.at, zone), today)].join(' · '));
    looks.push({
      id: a.id,
      kind: 'notDelivered',
      act: a.severity === 'needsAction',
      icon: 'xCircle',
      title: `${p?.first ?? 'A courier'}: ${plural(found.length, 'stop')} not delivered`,
      detail: `In the last 28 days · the oldest from ${dayBefore(dayOf(oldest, zone), today)}`,
      person: p,
      route: null,
      stop: null,
      alert: a,
      clearable: false,
      missed,
    });
  }
  looks.sort((a, b) => Number(b.act) - Number(a.act));
  // A past day the server has a record of takes its numbers, so the phone and the web read the same; a day without
  // one, or kept on another clock (a zone not confirmed), is worked out from the routes.
  const serverDays = new Set<string>();
  for (const [day, doc] of d.days ?? []) {
    if (day >= today || doc.zone !== zone) continue;
    const couriers = doc.couriers && typeof doc.couriers === 'object' ? (doc.couriers as Record<string, unknown>) : {};
    tallies.set(day, { all: readTally(doc.all), byCourier: new Map(Object.entries(couriers).map(([uid, t]) => [uid, readTally(t)])) });
    serverDays.add(day);
  }

  return {
    ready: d.status === 'ready',
    error: d.error,
    business,
    ownerName: d.ownerName,
    zone,
    now,
    today,
    day,
    people,
    person,
    routes: day.routes,
    route: (id) => routeMap.get(id) ?? null,
    raw: (id) => d.routes.get(id) ?? null,
    sending: new Set([...p.routes].filter(([, doc]) => doc).map(([id]) => id)),
    stops,
    stop: (key) => stopMap.get(key) ?? null,
    looks,
    place,
    shop,
    sentStops: d.sentStops,
    currency: business?.payments.currency ?? 'NGN',
    handInTotal: people.reduce((n, p) => n + Math.max(0, p.balance), 0),
    owedTotal: people.reduce((n, p) => n + Math.max(0, -p.balance), 0),
    history,
    tallies,
    serverDays,
    todayStart: dayStart(today, zone),
    yesterdayStart: dayStart(addDays(today, -1), zone),
    ifNow: ifNowOf(day.routes, d.routes, now),
    waiting: [...overlayWaiting(raw.waiting, p)]
      .map(([id, doc]) => readWaiting(id, doc, now))
      .filter((w): w is Waiting => !!w)
      .sort((a, b) => (b.addedAt ?? now) - (a.addedAt ?? now) || a.street.localeCompare(b.street)),
    zones: [...overlayMap(raw.zones, p.zones)]
      .map(([id, doc]) => readZone(id, doc))
      .filter((z): z is Zone => !!z)
      .sort((a, b) => (a.createdAt ?? now) - (b.createdAt ?? now) || a.name.localeCompare(b.name))
      .map((z) => {
        const c = z.couriers.map((uid) => personMap.get(uid)).find(Boolean);
        return { ...z, hex: c?.hex ?? NO_COLOUR.hex, on: c?.on ?? NO_COLOUR.on };
      }),
    data: d,
  };
}

/**
 * Routes waiting to start, timed as if they left now: the legs Google (or straight lines) gave when the route was sent or
 * changed (`optimization.leg_s`), and the courier's time at each stop. An estimate, said as one wherever it shows.
 */
function ifNowOf(routes: Route[], raws: Map<string, Doc>, now: number): Model['ifNow'] {
  const out: Model['ifNow'] = { stops: new Map(), routes: new Map() };
  for (const r of routes) {
    if (r.state !== 'waiting' || !r.stops.length) continue;
    const o = (raws.get(r.id)?.optimization ?? {}) as Record<string, unknown>;
    const legs = Array.isArray(o.leg_s) && o.leg_s.length === r.stops.length ? (o.leg_s as number[]) : null;
    const spots = r.stops.map((s) => s.spot);
    const t = legs
      ? { legS: legs, backS: typeof o.back_s === 'number' ? o.back_s : undefined, distanceM: Number(o.total_distance_m) || 0, drivingS: Number(o.total_duration_s) || legs.reduce((a, b) => a + b, 0) }
      : spots.every(Boolean)
        ? straight(r.start, r.stops.map((s) => ({ id: s.id, ...s.spot! })))
        : null;
    if (!t) continue;
    const dwell = r.timePerStopS || 180;
    const e = estimate(t, now, dwell);
    r.stops.forEach((s, i) => out.stops.set(keyOf(s), e.arrive[i]));
    out.routes.set(r.id, { finish: e.finish, distanceM: t.distanceM, durationS: Math.round((e.finish - now) / 1000), google: o.method === 'routesApi', back: o.round_trip === true });
  }
  return out;
}

/** The model, worked out again when the records change and as the clock moves (every 15 s). */
export function useModel(): Model {
  const d = useStore(data);
  const p = useStore(pending);
  const now = useNow(15_000);
  const m = useMemo(() => buildModel(d, now, p), [d, now, p]);
  current.m = m;
  return m;
}

/** The model as last drawn, for menus, ⌘K and actions that run outside React. */
export const current: { m: Model | null } = { m: null };

export type { Route, Stop, Day, Waiting };
